/* =============================================================================
   yojo-home-delivery-check: the homepage "מגיעים אליכם?" answer (2026-09-26).
   Markup: sections/yojo-home-delivery-check.liquid.

   ADVISORY ONLY, like the cart's city select. The checkout-validation Function
   (extensions/delivery-zone-validation) is the enforcement; this file only
   answers a question.

   WHERE THE ZONE COMES FROM: the <datalist> the section renders from
   snippets/yojo-gush-dan-cities.liquid. That list is the only allow-list.

   ALIASES below are NOT an allow-list. They are the spelling variants the
   Function already accepts (extensions/delivery-zone-validation/src/cities.ts:
   HEBREW_ALIASES, ENGLISH, ENGLISH_ALIASES), keyed by the city they belong to.
   An alias only counts when its city is in the rendered list, so removing a
   city from the snippet removes its aliases too. They exist so the homepage
   never says "no" to a spelling the checkout would accept ("תל אביב",
   "נוף ים", "Herzliya"). tests/unit/home-delivery-check.test.mjs fails if this
   table and cities.ts drift apart, in either direction.

   HOMEPAGE_ONLY (site sweep 2026-10-14, S-08) holds the few spellings
   shoppers type for an in-zone city that the Function does NOT accept yet:
   "ר"ג", the common misspelling "גבעתים" and "הרצליה פיתוח". Answering "no"
   to them told a Ramat Gan resident we do not deliver there. They follow the
   same rule (a spelling counts only while its city is listed), but they are a
   SEPARATE table because the Function refuses them as a typed checkout city
   when no usable postal code is given. The test lists them by name and fails
   the day the Function accepts one, so it moves into ALIASES instead.

   normalizeCity is a line-for-line port of cities.ts normalizeCity, for the
   same reason.

   THE CART'S CITY IS READ, NEVER WRITTEN. The cart keeps its answer in
   sessionStorage through assets/yojo-delivery-zone.js, which exposes a
   read-only API (window.yojoDeliveryZone.storedCity) and reads that answer at
   checkout-click time to show its early out-of-zone advisory. Writing to it
   from here would arm that advisory for someone who only asked a question on
   the homepage, so this file prefills from it and leaves it alone.
   ========================================================================== */
(function (root) {
  'use strict';

  var ALIASES = {
    'תל אביב-יפו': ['תל אביב', 'יפו', 'ת"א', 'tel aviv-yafo', 'tel aviv', 'tel aviv jaffa', 'jaffa', 'jafa'],
    'רמת גן': ['ramat gan'],
    'גבעתיים': ['givatayim', "giv'atayim"],
    'בני ברק': ['bnei brak', 'bene beraq', 'bnei braq'],
    'חולון': ['holon'],
    'בת ים': ['bat yam'],
    'הרצליה': ['הרצלייה', 'נוף ים', 'גליל ים', 'herzliya', 'herzliyya', 'hertzliya', 'herzlia', 'nof yam', 'galil yam'],
    'רמת השרון': ['ramat hasharon'],
    'קריית אונו': ['קרית אונו', 'kiryat ono', 'qiryat ono'],
    'גבעת שמואל': ['givat shmuel'],
    'יהוד-מונוסון': ['יהוד', 'yehud-monosson', 'yehud', 'yehud monosson', 'yehud monoson', 'yehud monossone'],
    'אזור': ['azor', 'azur'],
    'ראשון לציון': ['ראשל"צ', 'rishon lezion', 'rishon le zion', 'rishon letsiyon', 'rishon letzion'],
    'הוד השרון': ['hod hasharon'],
    'כפר שמריהו': ['kfar shmaryahu', 'kfar shemaryahu'],
    'רעננה': ['raanana', "ra'anana", 'ranana', 'ra anana']
  };

  // Not accepted by the Function yet; see HOMEPAGE_ONLY in the header.
  var HOMEPAGE_ONLY = {
    'רמת גן': ['ר"ג'],
    'גבעתיים': ['גבעתים'],
    'הרצליה': ['הרצליה פיתוח']
  };

  var PLACEHOLDER = '[עיר]';
  var MAX_SUGGESTIONS = 4;

  // Port of extensions/delivery-zone-validation/src/cities.ts normalizeCity.
  function normalizeCity(city) {
    if (!city) return '';
    return String(city)
      .normalize('NFKC')
      .replace(/[\u0591-\u05C7]/g, '')
      .replace(/["'`\u05F3\u05F4\u2018\u2019\u201A\u201B\u201C\u201D]/g, '')
      .replace(/[\u05BE\-\u2013\u2014_.,/\\()]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function keyOf(value) {
    return normalizeCity(value).toLowerCase();
  }

  // index: normalized spelling -> the city name exactly as the list prints it.
  // names: the same pairs in list order, for the "did you mean" suggestions.
  function buildIndex(cities) {
    var index = Object.create(null);
    var names = [];
    function add(spelling, city) {
      var k = keyOf(spelling);
      if (!k || index[k]) return;
      index[k] = city;
      names.push([k, city]);
    }
    (cities || []).forEach(function (c) {
      var city = String(c == null ? '' : c).trim();
      if (city) add(city, city);
    });
    [ALIASES, HOMEPAGE_ONLY].forEach(function (table) {
      Object.keys(table).forEach(function (target) {
        var city = index[keyOf(target)];
        if (!city) return; // not in the live list: its aliases widen nothing
        table[target].forEach(function (alias) { add(alias, city); });
      });
    });
    return { index: index, names: names };
  }

  /**
   * matchCity(input, cities) -> { status, city, input, suggestions }
   *   'empty'   nothing (or a single letter) typed
   *   'in'      a listed city or one of its spellings; city = the listed name
   *   'suggest' the start of one or more listed names ("רמת", "ראשון")
   *   'out'     anything else: not a city we deliver to
   */
  function matchCity(input, cities) {
    var raw = String(input == null ? '' : input).trim();
    var k = keyOf(raw);
    var result = { status: 'empty', city: '', input: raw, suggestions: [] };
    if (k.length < 2) return result;
    var built = buildIndex(cities);
    if (built.index[k]) {
      result.status = 'in';
      result.city = built.index[k];
      return result;
    }
    built.names.forEach(function (pair) {
      if (pair[0].indexOf(k) === 0 && result.suggestions.indexOf(pair[1]) === -1) {
        result.suggestions.push(pair[1]);
      }
    });
    result.suggestions = result.suggestions.slice(0, MAX_SUGGESTIONS);
    result.status = result.suggestions.length ? 'suggest' : 'out';
    return result;
  }

  function fillCity(template, city) {
    return String(template || '').split(PLACEHOLDER).join(city);
  }

  var api = {
    ALIASES: ALIASES,
    HOMEPAGE_ONLY: HOMEPAGE_ONLY,
    normalizeCity: normalizeCity,
    matchCity: matchCity,
    fillCity: fillCity
  };
  if (typeof module === 'object' && module && module.exports) module.exports = api;
  root.YojoHomeDeliveryCheck = api;

  if (typeof document === 'undefined') return;

  /* ---------------------------------------------------------------- DOM -- */

  function storedCartCity() {
    var zone = root.yojoDeliveryZone;
    if (!zone || typeof zone.storedCity !== 'function') return '';
    try { return String(zone.storedCity() || ''); } catch (e) { return ''; }
  }

  function readCopy(el) {
    var node = el.querySelector('[data-yhdc-copy]');
    try { return JSON.parse(node ? node.textContent : '{}') || {}; } catch (e) { return {}; }
  }

  function init(el) {
    if (!el || el.getAttribute('data-yhdc-ready') === '1') return;
    var form = el.querySelector('[data-yhdc-form]');
    var input = el.querySelector('[data-yhdc-input]');
    var verdict = el.querySelector('[data-yhdc-verdict]');
    if (!form || !input || !verdict) return;
    el.setAttribute('data-yhdc-ready', '1');

    var copy = readCopy(el);
    var cities = Array.prototype.map.call(
      el.querySelectorAll('datalist option'),
      function (o) { return o.value; }
    );
    var panels = {
      in: el.querySelector('[data-yhdc-panel="in"]'),
      out: el.querySelector('[data-yhdc-panel="out"]'),
      suggest: el.querySelector('[data-yhdc-panel="suggest"]')
    };
    var suggestList = el.querySelector('[data-yhdc-suggestions]');
    var wlCity = el.querySelector('[data-yhdc-wl-city]');
    var wlBody = el.querySelector('[data-yhdc-wl-body]');
    var announceTimer = null;
    var shown = '';

    function setVerdict(text, state, announce) {
      verdict.className = 'yhdc__verdict' + (state ? ' is-' + state : '');
      if (announceTimer) clearTimeout(announceTimer);
      if (!announce) { verdict.textContent = text; return; }
      // Clear, then write on the next tick, so a repeated identical answer is
      // still announced by screen readers that ignore same-text updates.
      verdict.textContent = '';
      announceTimer = setTimeout(function () { verdict.textContent = text; }, 60);
    }

    function hidePanels() {
      Object.keys(panels).forEach(function (k) { if (panels[k]) panels[k].hidden = true; });
    }

    function idle() {
      if (!shown) return;
      shown = '';
      hidePanels();
      input.removeAttribute('aria-invalid');
      setVerdict('', '', false);
    }

    function prefillWaitlist(city) {
      if (wlCity) wlCity.value = city;
      if (wlBody) wlBody.value = 'waitlist | city: ' + (city || '?') + ' | source: home-delivery-check';
    }

    function renderSuggestions(list) {
      if (!suggestList) return;
      suggestList.textContent = '';
      list.forEach(function (city) {
        var li = document.createElement('li');
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'yhdc__chip';
        btn.textContent = city;
        btn.addEventListener('click', function () {
          input.value = city;
          show(matchCity(city, cities), true);
          input.focus();
        });
        li.appendChild(btn);
        suggestList.appendChild(li);
      });
    }

    function show(result, announce) {
      hidePanels();
      input.removeAttribute('aria-invalid');
      shown = result.status;
      if (result.status === 'in') {
        setVerdict(fillCity(copy.yes, result.city), 'in', announce);
        if (panels.in) panels.in.hidden = false;
      } else if (result.status === 'suggest') {
        renderSuggestions(result.suggestions);
        setVerdict(copy.suggest || '', 'suggest', announce);
        if (panels.suggest) panels.suggest.hidden = false;
      } else if (result.status === 'out') {
        setVerdict(copy.out || '', 'out', announce);
        prefillWaitlist(result.input);
        if (panels.out) panels.out.hidden = false;
      } else {
        input.setAttribute('aria-invalid', 'true');
        setVerdict(copy.empty || '', 'error', announce);
      }
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var result = matchCity(input.value, cities);
      show(result, true);
      if (result.status === 'empty') input.focus();
    });

    // A pick from the datalist, or a full city name typed out, answers at
    // once. Anything partial waits for the button, and clears a stale answer.
    input.addEventListener('input', function () {
      var result = matchCity(input.value, cities);
      if (result.status === 'in') show(result, true);
      else idle();
    });

    // Contact form came back from Shopify (sent, or with an error): reopen
    // the out-of-zone panel it lives in, and bring it into view.
    if (panels.out && panels.out.querySelector('[data-yhdc-wl-state]')) {
      panels.out.hidden = false;
      shown = 'out';
      var wlForm = panels.out.querySelector('form');
      if (wlForm && wlForm.id && root.location && root.location.hash === '#' + wlForm.id) {
        try { wlForm.scrollIntoView({ block: 'center' }); } catch (e) { wlForm.scrollIntoView(); }
      }
      return;
    }

    // Agree with the cart: if the shopper already named a delivery city there
    // this session, answer for it. "__other__" names no city, so it is skipped.
    var stored = storedCartCity();
    if (stored && stored !== '__other__') {
      var prior = matchCity(stored, cities);
      if (prior.status === 'in') {
        input.value = prior.city;
        show(prior, false);
      }
    }
  }

  function initAll() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-yhdc]'), init);
  }

  // The cart API (yojo-delivery-zone.js) is a deferred script rendered after
  // this section in document order, so wait for DOMContentLoaded, when every
  // deferred script has run. `load` is a second, idempotent chance.
  if (document.readyState === 'complete') initAll();
  else document.addEventListener('DOMContentLoaded', initAll);
  root.addEventListener('load', initAll);
  document.addEventListener('shopify:section:load', initAll);
})(typeof window !== 'undefined' ? window : globalThis);
